import { Ban, ChevronRight, Pause, Play, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { CampaignStatusBadge, LeadStatusBadge } from '../components/ui/data';
import { ConfirmDialog, Drawer, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  Progress,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui/primitives';
import {
  useCampaign,
  useCampaigns,
  useDeleteCampaign,
  useUpdateCampaign,
} from '../hooks/queries';
import { formatDateTime, formatNumber, formatRelative, titleCase } from '../lib/utils';

export const CampaignsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { data, isLoading } = useCampaigns(projectId);
  const updateCampaign = useUpdateCampaign(projectId);
  const deleteCampaign = useDeleteCampaign();
  const toast = useToast();

  const [openId, setOpenId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const campaigns = data?.items ?? [];

  const setStatus = async (id: string, status: string, label: string) => {
    try {
      await updateCampaign.mutateAsync({ id, status });
      toast.success(label);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Outreach"
        description="Every batch you queued. An MCP outreach agent claims the tasks and writes one email per lead."
        actions={
          <Link to={`/projects/${projectId}/leads`}>
            <Button variant="primary" icon={<Send className="h-4 w-4" />}>
              Queue new outreach
            </Button>
          </Link>
        }
      />

      {campaigns.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Send className="h-5 w-5" />}
            title="No outreach queued yet"
            description="Go to Leads, select the ones worth writing to, and click Queue outreach. That creates the tasks an agent picks up."
            action={
              <Link to={`/projects/${projectId}/leads`}>
                <Button variant="primary">Pick leads</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Campaign</Th>
                <Th>Status</Th>
                <Th className="w-52">Progress</Th>
                <Th>Approval</Th>
                <Th>Mailbox</Th>
                <Th>Created</Th>
                <Th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const progress = campaign.progress ?? {
                  total: 0,
                  pending: 0,
                  drafted: 0,
                  sent: 0,
                  skipped: 0,
                };
                const done = progress.total - progress.pending;
                return (
                  <Tr key={campaign.id}>
                    <Td>
                      <button
                        type="button"
                        onClick={() => setOpenId(campaign.id)}
                        className="text-left font-medium text-ink hover:text-brand"
                      >
                        {campaign.name}
                      </button>
                      {campaign.leadType ? (
                        <div className="mt-0.5">
                          <Badge dot={campaign.leadType.color}>{campaign.leadType.name}</Badge>
                        </div>
                      ) : null}
                    </Td>
                    <Td>
                      <CampaignStatusBadge status={campaign.status} />
                    </Td>
                    <Td>
                      <div className="mb-1 flex items-center justify-between text-xs text-muted">
                        <span>
                          {formatNumber(done)} / {formatNumber(progress.total)} handled
                        </span>
                        <span className="tabular-nums text-faint">
                          {formatNumber(progress.sent)} sent
                        </span>
                      </div>
                      <Progress value={done} max={progress.total || 1} />
                    </Td>
                    <Td>
                      {campaign.requireApproval ? (
                        <Badge tone="warning">review first</Badge>
                      ) : (
                        <Badge tone="positive">auto-send</Badge>
                      )}
                    </Td>
                    <Td className="text-muted">{campaign.mailbox?.fromEmail ?? '—'}</Td>
                    <Td className="whitespace-nowrap text-faint">
                      {formatRelative(campaign.createdAt)}
                    </Td>
                    <Td>
                      <div className="flex items-center justify-end gap-1">
                        {campaign.status === 'PAUSED' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Resume"
                            onClick={() => setStatus(campaign.id, 'QUEUED', 'Campaign resumed')}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        ) : campaign.status === 'QUEUED' || campaign.status === 'RUNNING' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Pause"
                              onClick={() =>
                                setStatus(
                                  campaign.id,
                                  'PAUSED',
                                  'Campaign paused — scheduled emails moved back to drafts',
                                )
                              }
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Cancel"
                              onClick={() =>
                                setStatus(campaign.id, 'CANCELLED', 'Campaign cancelled')
                              }
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          </>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete"
                          onClick={() => setDeleteTarget({ id: campaign.id, name: campaign.name })}
                        >
                          <Trash2 className="h-4 w-4 text-danger" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Details"
                          onClick={() => setOpenId(campaign.id)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <CampaignDrawer campaignId={openId} onClose={() => setOpenId(null)} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete campaign"
        destructive
        confirmLabel="Delete"
        loading={deleteCampaign.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.name}</strong>? Its tasks go with it.
            Emails already sent are kept.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteCampaign.mutateAsync(deleteTarget.id);
            toast.success('Campaign deleted');
            setDeleteTarget(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};

const CampaignDrawer = ({
  campaignId,
  onClose,
}: {
  campaignId: string | null;
  onClose: () => void;
}) => {
  const { data: campaign } = useCampaign(campaignId ?? undefined);

  return (
    <Drawer
      open={Boolean(campaignId)}
      onClose={onClose}
      width="lg"
      title={campaign?.name ?? 'Campaign'}
      subtitle={
        campaign ? (
          <span className="flex flex-wrap items-center gap-2">
            <CampaignStatusBadge status={campaign.status} />
            <span className="text-2xs text-faint">
              created {formatDateTime(campaign.createdAt)}
            </span>
          </span>
        ) : null
      }
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {!campaign ? (
        <div className="p-5 text-[13px] text-faint">Loading…</div>
      ) : (
        <div className="divide-y divide-line">
          <div className="grid grid-cols-2 gap-3 px-5 py-4 text-[13px] sm:grid-cols-4">
            {[
              ['Total', campaign.progress?.total ?? 0],
              ['Waiting', campaign.progress?.pending ?? 0],
              ['Drafted', campaign.progress?.drafted ?? 0],
              ['Sent', campaign.progress?.sent ?? 0],
            ].map(([label, value]) => (
              <div key={String(label)}>
                <div className="text-2xs uppercase tracking-wide text-faint">{label}</div>
                <div className="text-lg font-semibold tabular-nums text-ink">
                  {formatNumber(Number(value))}
                </div>
              </div>
            ))}
          </div>

          {campaign.instructions ? (
            <div className="px-5 py-4">
              <span className="field-label">Batch instructions</span>
              <pre className="whitespace-pre-wrap rounded-lg bg-raised px-3 py-2.5 font-mono text-[12px] leading-relaxed text-muted">
                {campaign.instructions}
              </pre>
            </div>
          ) : null}

          <div className="px-5 py-4">
            <span className="field-label">Settings</span>
            <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Approval</dt>
                <dd className="text-ink">{campaign.requireApproval ? 'Required' : 'Auto-send'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Spacing</dt>
                <dd className="text-ink">{Math.round(campaign.spacingSeconds / 60)} min</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Mailbox</dt>
                <dd className="truncate text-ink">{campaign.mailbox?.fromEmail ?? 'default'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Starts</dt>
                <dd className="text-ink">
                  {campaign.scheduledStartAt ? formatDateTime(campaign.scheduledStartAt) : 'as soon as ready'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="px-5 py-4">
            <span className="field-label">Tasks ({campaign.tasks?.length ?? 0})</span>
            <div className="space-y-1.5">
              {(campaign.tasks ?? []).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13px] text-ink">{task.lead.companyName}</div>
                    <div className="truncate text-2xs text-faint">
                      {task.lead.email ?? 'no email'}
                      {task.claimedBy ? ` · claimed by ${task.claimedBy}` : ''}
                      {task.error ? ` · ${task.error}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <LeadStatusBadge status={task.lead.status} />
                    <Badge
                      tone={
                        task.status === 'DRAFTED'
                          ? 'positive'
                          : task.status === 'PENDING'
                            ? 'warning'
                            : task.status === 'CLAIMED'
                              ? 'brand'
                              : 'muted'
                      }
                    >
                      {titleCase(task.status)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};
